import type { NexusThemeMode } from '../hooks/useLayoutPreferences';

export type NexusResolvedTheme = 'light' | 'dark';

const STORAGE_PREFIX = 'nexus-layout-prefs';

function isThemeMode(value: unknown): value is NexusThemeMode {
    return value === 'light' || value === 'dark' || value === 'system';
}

/** Resolve `system` via `prefers-color-scheme`; demais modos são diretos. */
export function resolveNexusTheme(mode: NexusThemeMode): NexusResolvedTheme {
    if (mode === 'light' || mode === 'dark') {
        return mode;
    }

    if (typeof window === 'undefined') {
        return 'light';
    }

    return window.matchMedia('(prefers-color-scheme: dark)').matches
        ? 'dark'
        : 'light';
}

/** Aplica tema resolvido no `<html>` (data-theme + color-scheme nativo). */
export function applyNexusThemeToDocument(resolved: NexusResolvedTheme): void {
    if (typeof document === 'undefined') {
        return;
    }

    document.documentElement.dataset.theme = resolved;
    document.documentElement.style.colorScheme = resolved;
}

export function applyNexusThemeFromPreference(mode: NexusThemeMode): void {
    applyNexusThemeToDocument(resolveNexusTheme(mode));
}

function storageKey(userId: number | string | null | undefined): string {
    return `${STORAGE_PREFIX}:${userId ?? 'guest'}`;
}

function readThemeFromKey(key: string): NexusThemeMode {
    if (typeof window === 'undefined') {
        return 'light';
    }

    try {
        const raw = window.localStorage.getItem(key);
        if (!raw) {
            return 'light';
        }
        const parsed = JSON.parse(raw) as { theme?: unknown };
        return isThemeMode(parsed.theme) ? parsed.theme : 'light';
    } catch {
        return 'light';
    }
}

/** Lê userId do payload Inertia em `#app[data-page]` (boot antes do React). */
export function readInertiaUserIdFromDom(): number | string | null {
    if (typeof document === 'undefined') {
        return null;
    }

    try {
        const raw = document.getElementById('app')?.getAttribute('data-page');
        if (!raw) {
            return null;
        }
        const page = JSON.parse(raw) as {
            props?: { auth?: { user?: { id?: number | string } } };
        };
        return page.props?.auth?.user?.id ?? null;
    } catch {
        return null;
    }
}

/** Evita flash de tema errado antes da hidratação React. */
export function bootstrapNexusThemeFromStorage(
    userId: number | string | null = readInertiaUserIdFromDom(),
): void {
    const theme = readThemeFromKey(storageKey(userId));
    applyNexusThemeFromPreference(theme);
}
